/**
 * E2EE Crypto Utilities — Global Shared Key
 *
 * All users share a single AES-256-GCM key set by the admin.
 * The key is fetched from the server and used for all encrypt/decrypt ops.
 */

// ── Import shared key from base64 ───────────────────────────────────────────

/**
 * Import a base64-encoded AES-256 key into a CryptoKey.
 * @param {string} base64Key
 * @returns {Promise<CryptoKey>}
 */
export async function importSharedKey(base64Key) {
  const rawBytes = base64ToBuf(base64Key);
  return crypto.subtle.importKey(
    'raw',
    rawBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ── Encrypt / Decrypt ────────────────────────────────────────────────────────

/**
 * Encrypt a plaintext message with the shared AES key.
 * @param {string} plaintext
 * @param {CryptoKey} aesKey
 * @returns {{ ciphertext: string, iv: string }} base64-encoded values
 */
export async function encryptMessage(plaintext, aesKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);

  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    encoded
  );

  return {
    ciphertext: bufToBase64(cipherBuf),
    iv: bufToBase64(iv),
  };
}

/**
 * Decrypt an encrypted message with the shared AES key.
 * @param {string} ciphertext - base64
 * @param {string} iv - base64
 * @param {CryptoKey} aesKey
 * @returns {string} plaintext
 */
export async function decryptMessage(ciphertext, iv, aesKey) {
  const cipherBuf = base64ToBuf(ciphertext);
  const ivBuf = base64ToBuf(iv);

  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivBuf },
    aesKey,
    cipherBuf
  );

  return new TextDecoder().decode(plainBuf);
}

// ── Base64 Helpers ───────────────────────────────────────────────────────────

function bufToBase64(buf) {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBuf(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
